import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';
import LineChart from '../components/LineChart';
import { presetRange, rangeText, daysBetween, addDays, weekLabel, weekText, longDate, parse } from '../dateRange';

const CHANNEL_HE = {
  'Organic Search': 'חיפוש אורגני', 'Direct': 'ישיר', 'Referral': 'הפניות מאתרים', 'Organic Social': 'סושיאל אורגני',
  'Paid Search': 'חיפוש ממומן', 'Paid Social': 'סושיאל ממומן', 'Email': 'אימייל', 'Unassigned': 'לא משויך',
  'Display': 'מדיה (Display)', 'Organic Video': 'וידאו אורגני', 'Paid Video': 'וידאו ממומן', 'Cross-network': 'קמפיינים חוצי רשתות',
  'Paid Other': 'ממומן אחר', 'Organic Shopping': 'שופינג אורגני', 'Paid Shopping': 'שופינג ממומן', 'Affiliates': 'שותפים',
  'SMS': 'SMS', 'Mobile Push Notifications': 'התראות פוש', 'Audio': 'אודיו',
};
const COLORS = ['var(--accent)', 'var(--green)', 'var(--yellow)', 'var(--red)', 'var(--orange)', 'var(--text-secondary)'];
const CHART_SERIES = 6;
// Up to this many days the chart shows days; beyond it, 7-day sums.
const DAILY_MAX = 45;

function Change({ value }) {
  if (value == null) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>חדש</span>;
  return (
    <span className={'change-badge ' + (value >= 0 ? 'up' : 'down')}>
      {value >= 0 ? '▲' : '▼'} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function sourcesChart(report) {
  const { start, end, daily, rows } = report;
  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  const weekly = days.length > DAILY_MAX;
  // Weeks are counted back from the last day, so the newest point is always a full week.
  const buckets = [];
  if (weekly) {
    for (let e = end; addDays(e, -6) >= start; e = addDays(e, -7)) buckets.unshift(e);
  }
  const top = rows.map(r => r.key).filter(k => daily[k]).slice(0, CHART_SERIES);
  const series = top.map((key, i) => ({
    label: CHANNEL_HE[key] || key,
    color: COLORS[i % COLORS.length],
    values: weekly
      ? buckets.map(e => { let s = 0; for (let k = 0; k < 7; k++) s += daily[key][addDays(e, -k)] || 0; return s; })
      : days.map(d => daily[key][d] || 0),
  }));
  const dayLabel = d => { const x = parse(d); return x.getDate() + '/' + (x.getMonth() + 1); };
  return {
    weekly,
    leftover: weekly && buckets.length ? daysBetween(start, addDays(buckets[0], -7)) : 0,
    labels: weekly ? buckets.map(weekLabel) : days.map(dayLabel),
    hoverLabels: weekly ? buckets.map(weekText) : days.map(longDate),
    series,
  };
}

export default function SiteAnalytics() {
  const { id } = useParams();
  const [range, setRange] = useState(() => presetRange('28'));
  const [by, setBy] = useState('channel');
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sourcesError, setSourcesError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.getGa4Summary(id, null, range.start, range.end)
      .then(r => { if (alive) setData(r); })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, range.start, range.end]);

  useEffect(() => {
    let alive = true;
    setSourcesLoading(true);
    setSourcesError(null);
    api.getGa4SourcesReport(id, range.start, range.end, by)
      .then(r => { if (alive) setSources(r); })
      .catch(err => { if (alive) setSourcesError(err.message); })
      .finally(() => { if (alive) setSourcesLoading(false); });
    return () => { alive = false; };
  }, [id, range.start, range.end, by]);

  const bar = (
    <DateRangeBar value={range} onChange={setRange} presets={[7, 14, 28, 90, 'this_month', 'last_month', 365]}
      note={sources ? 'השוואה לתקופה הקודמת: ' + rangeText(sources.prev_start, sources.prev_end) : null} />
  );

  if (error) return (
    <>
      {bar}
      <div className="placeholder-page">
        <div className="icon">🔄</div>
        <h2>צריך לחבר מחדש את Google Analytics</h2>
        <p>ה-OAuth token פג תוקף. יש להתחבר מחדש דרך הגדרות האתר.</p>
        <p style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'12px'}}>{error}</p>
      </div>
    </>
  );

  const chart = sources && sources.rows.length ? sourcesChart(sources) : null;
  const dim = loading ? { opacity: 0.5, transition: 'opacity .2s' } : null;

  return (
    <>
      {bar}

      {!data ? <div className="loading">טוען נתוני Analytics...</div> : (
        <div className="site-overview-header" style={dim}>
          <div className="site-metric-card animate-in stagger-1">
            <div className="label">סשנים ({daysBetween(range.start, range.end)} ימים)</div>
            <div className="value-row">
              <div className="value" style={{color:'var(--accent)'}}>{(data.sessions || 0).toLocaleString()}</div>
              {data.sessions_change != null && <Change value={data.sessions_change} />}
            </div>
          </div>
          <div className="site-metric-card animate-in stagger-2">
            <div className="label">משתמשים</div>
            <div className="value-row">
              <div className="value">{(data.users || 0).toLocaleString()}</div>
            </div>
          </div>
          <div className="site-metric-card animate-in stagger-3">
            <div className="label">צפיות עמוד</div>
            <div className="value-row">
              <div className="value" style={{color:'var(--green)'}}>{(data.pageviews || 0).toLocaleString()}</div>
            </div>
          </div>
          <div className="site-metric-card animate-in stagger-4">
            <div className="label">Bounce Rate</div>
            <div className="value-row">
              <div className="value" style={{color:'var(--orange)'}}>{data.bounce_rate != null ? (data.bounce_rate * 100).toFixed(1) + '%' : '-'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Traffic sources report */}
      <div className="section-card animate-in">
        <div className="section-header">
          <div className="section-title">מקורות תנועה</div>
          <div className="section-actions">
            <button className={'filter-chip' + (by === 'channel' ? ' active' : '')} onClick={() => setBy('channel')}>לפי ערוץ</button>
            <button className={'filter-chip' + (by === 'source_medium' ? ' active' : '')} onClick={() => setBy('source_medium')}>לפי מקור / מדיום</button>
          </div>
        </div>
        {sourcesError ? (
          <div style={{ padding: 20, fontSize: 13, color: 'var(--red)' }}>{sourcesError}</div>
        ) : !sources ? (
          <div className="loading">טוען מקורות תנועה...</div>
        ) : (
          <div style={sourcesLoading ? { opacity: 0.5, transition: 'opacity .2s' } : null}>
            {chart && (
              <div style={{ padding: '14px 20px 6px' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {chart.weekly ? 'סשנים שבועיים לפי ערוץ' : 'סשנים יומיים לפי ערוץ'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {chart.weekly
                    ? 'כל נקודה = סכום 7 ימים, מסומנת בטווח התאריכים שלה (יום–יום/חודש). '
                      + (chart.leftover > 0 ? chart.leftover + ' הימים הראשונים בטווח לא משלימים שבוע ולא מוצגים בגרף. ' : '')
                      + 'עברו עם העכבר לתאריכים המלאים.'
                    : 'כל נקודה = יום אחד (יום/חודש). עברו עם העכבר לתאריך המלא.'}
                </div>
                <LineChart labels={chart.labels} hoverLabels={chart.hoverLabels} series={chart.series} height={200} />
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className="rankings-table">
                <thead>
                  <tr>
                    <th>{by === 'channel' ? 'ערוץ' : 'מקור / מדיום'}</th>
                    <th>סשנים</th>
                    <th>% מהתנועה</th>
                    <th>שינוי</th>
                    <th>משתמשים</th>
                    <th>חדשים</th>
                    <th>מעורבות</th>
                    <th>זמן ממוצע</th>
                    <th>המרות</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.rows.map(r => (
                    <tr key={r.key}>
                      <td>
                        {by === 'channel'
                          ? <><span className="keyword-text">{CHANNEL_HE[r.key] || r.key}</span>
                              {CHANNEL_HE[r.key] && <span dir="ltr" style={{ fontSize: 11, color: 'var(--text-muted)', marginInlineStart: 6 }}>{r.key}</span>}</>
                          : <span dir="ltr" className="keyword-text">{r.key}</span>}
                      </td>
                      <td><span className="metric-val">{r.sessions.toLocaleString()}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 60, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: r.share + '%', height: '100%', background: 'var(--accent)' }} />
                          </div>
                          <span className="metric-val">{r.share}%</span>
                        </div>
                      </td>
                      <td><Change value={r.sessions_change} /></td>
                      <td><span className="metric-val">{r.users.toLocaleString()}</span></td>
                      <td><span className="metric-val">{r.new_users.toLocaleString()}</span></td>
                      <td><span className="metric-val">{r.engagement_rate}%</span></td>
                      <td><span className="metric-val">{Math.round(r.avg_session_duration)} ש׳</span></td>
                      <td><span className="metric-val">{r.key_events.toLocaleString()}</span></td>
                    </tr>
                  ))}
                  {!sources.rows.length && (
                    <tr><td colSpan={9} style={{ color: 'var(--text-muted)', textAlign: 'center' }}>אין תנועה בטווח שנבחר</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Summary details */}
      {data && (
        <div className="section-card animate-in">
          <div className="section-header">
            <div className="section-title">נתונים נוספים</div>
          </div>
          <table className="rankings-table">
            <tbody>
              <tr>
                <td style={{color:'var(--text-muted)',width:'200px'}}>משתמשים חדשים</td>
                <td><span className="metric-val">{(data.new_users || 0).toLocaleString()}</span></td>
              </tr>
              <tr>
                <td style={{color:'var(--text-muted)'}}>זמן ממוצע בסשן</td>
                <td><span className="metric-val">{data.avg_session_duration ? Math.round(data.avg_session_duration) + ' שניות' : '-'}</span></td>
              </tr>
              <tr>
                <td style={{color:'var(--text-muted)'}}>שינוי סשנים</td>
                <td>
                  <Change value={data.sessions_change || 0} />
                  <span style={{fontSize:'12px',color:'var(--text-muted)',marginRight:'8px'}}>מהתקופה הקודמת</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
